import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { useObligaciones } from '@/hooks/useObligaciones';
import { Badge } from '@/components/ui/badge';
import logoViable from '@/assets/logo-viabiliza.png';
import { useAuth } from '@/hooks/useAuth';

export default function ReporteAmbiental() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [tipoFiltro, setTipoFiltro] = useState<'Ambiental' | 'Biótico' | 'Abiótico'>('Ambiental');
  const { obligaciones, loading } = useObligaciones(tipoFiltro);

  const handleGenerarReporte = (obligacionId: string) => {
    navigate(`/reporte-ambiental/${obligacionId}`);
  };

  const getEstadoBadge = (estado: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'Cumplido': 'default',
      'En Proceso': 'secondary',
      'Pendiente': 'outline',
      'Vencido': 'destructive',
    };
    return variants[estado] || 'outline';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-b border-border z-50 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={logoViable} alt="Viabiliza" className="h-12" />
              <div>
                <h1 className="text-xl font-bold text-foreground">Reportes Ambientales</h1>
                <p className="text-sm text-muted-foreground">Formato ICA - Cumplimiento del PMA</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/reporte-integrado')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a Reportes
              </Button>
              <Button variant="destructive" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-32 lg:pt-24 pb-12">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Filtros */}
          <Card>
            <CardHeader>
              <CardTitle>Seleccionar Tipo de Obligación</CardTitle>
              <CardDescription>
                Filtra las obligaciones por tipo para generar el reporte correspondiente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={tipoFiltro}
                onValueChange={(value) => setTipoFiltro(value as 'Ambiental' | 'Biótico' | 'Abiótico')}
              >
                <SelectTrigger className="w-full md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ambiental">🌍 Ambiental</SelectItem>
                  <SelectItem value="Biótico">🌱 Biótico</SelectItem>
                  <SelectItem value="Abiótico">🪨 Abiótico</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Lista de Obligaciones */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : obligaciones.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-muted-foreground">
                  No hay obligaciones {tipoFiltro.toLowerCase()} registradas
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {obligaciones.map((obligacion) => (
                <Card key={obligacion.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="text-base line-clamp-2">
                          {obligacion.nombre_programa}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Código: {obligacion.codigo}
                        </CardDescription>
                      </div>
                      <Badge variant={getEstadoBadge(obligacion.estado)}>
                        {obligacion.estado}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tipo:</span>
                        <span className="font-medium">{obligacion.tipo}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Frecuencia:</span>
                        <span className="font-medium">{obligacion.frecuencia}</span>
                      </div>
                      {obligacion.nivel_cumplimiento !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Cumplimiento:</span>
                          <span className="font-medium">{obligacion.nivel_cumplimiento}%</span>
                        </div>
                      )}
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={() => handleGenerarReporte(obligacion.id)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Generar Reporte
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
